import { useEffect, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Input,
  Label,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap";

import { useNavigate, useParams } from "react-router-dom";

import { useFormik } from "formik";

import schema, { MAX_PRODUCT_IMAGES } from "./schema";
import {
  FileDropzone,
  DEFAULT_MAX_FILE_BYTES,
} from "components/common/FileDropzone";
import { formatFileSize } from "utils/fileUtils";
import { toast } from "react-toastify";
import {
  createProduct,
  getProduct,
  updateProduct,
} from "features/Products/services/ProductService";
import { Product } from "features/Products/types/product.types";
import { BackButton } from "components/common/Buttons";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import {
  getProductCategoryOptions,
  getProductTypeOptions,
  getUomOptions,
} from "features/Meta/services/MetaService";
import { getMaterialOptions } from "features/Meta/services/MetaService";
import { getDocumentTitle } from "utils/helpers";

const API_BASE = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

const PRODUCT_IMAGE_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
} as const;

function resolveImageSrc(pathOrUrl: string): string {
  const t = pathOrUrl.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.replace(/^\//, "");
  if (!API_BASE) return `/${path}`;
  return `${API_BASE}/${path}`;
}

function imageLabel(url: string): string {
  try {
    if (/^https?:\/\//i.test(url)) {
      const u = new URL(url);
      const last = u.pathname.split("/").filter(Boolean).pop();
      return last ? decodeURIComponent(last) : url;
    }
  } catch {
    /* ignore */
  }
  const seg = url.split("/").pop();
  return decodeURIComponent(seg || url);
}

type FileWithPreview = File & { preview?: string };

function isImageMime(file: File): boolean {
  return Boolean(file.type && file.type.startsWith("image/"));
}

const ProductForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(!isEdit ? "New Product" : "Edit Product");
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [selectedFiles, setselectedFiles] = useState<FileWithPreview[]>([]);
  const [deletedFiles, setDeletedFiles] = useState<number[]>([]);
  const [existingFiles, setExistingFiles] = useState<string[]>([]);
  const selectedFilesRef = useRef<FileWithPreview[]>([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const [record, setRecord] = useState<Product>();

  selectedFilesRef.current = selectedFiles;

  useEffect(() => {
    return () => {
      selectedFilesRef.current.forEach((f) => {
        if (f.preview?.startsWith("blob:")) URL.revokeObjectURL(f.preview);
      });
    };
  }, []);

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: record?.name ?? "",
      description: record?.description ?? "",
      categoryId: record?.categoryId ?? "",
      materialId:
        record?.materialId != null && record.materialId > 0
          ? String(record.materialId)
          : "",
      specification: record?.specification ?? "",
      productTypeId: record?.productTypeId ?? "",
      purchaseCost: record?.purchaseCost ?? 0,
      standardRate: record?.standardRate ?? 0,
      isActive: record?.isActive ?? true,
      manufacturer: record?.manufacturer ?? "",
      brand: record?.brand ?? "",
      images: [] as File[],
      deletedFiles: [] as number[],
      existingFiles,
      uomId: record?.uomId ?? "",
    },
    validationSchema: schema,
    validateOnMount: false,
    validateOnChange: true,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("categoryId", String(values.categoryId));
      if (
        values.materialId !== "" &&
        values.materialId != null &&
        String(values.materialId).trim() !== ""
      ) {
        formData.append("materialId", String(values.materialId));
      }
      formData.append("specification", values.specification ?? "");
      formData.append("productTypeId", String(values.productTypeId));
      formData.append("isActive", String(values.isActive ?? false));
      formData.append("manufacturer", values.manufacturer);
      formData.append("brand", values.brand);
      formData.append("purchaseCost", String(values.purchaseCost));
      formData.append("standardRate", String(values.standardRate));
      formData.append("uomId", String(values.uomId));
      if (isEdit) {
        formData.append("deletedImages", JSON.stringify(deletedFiles));
      }

      values.images?.forEach((file: File) => {
        formData.append("images", file);
      });

      try {
        let response;
        if (isEdit) {
          response = await updateProduct(Number(id), formData);
        } else {
          response = await createProduct(formData);
        }

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          "Something went wrong";
        toast.error(msg);
        validation.setFieldError("images", msg);
        void validation.setFieldTouched("images", true, false);
      }
    },
  });

  const appendDroppedFiles = (incoming: File[]) => {
    const existingLen = existingFiles.length;
    const prev = selectedFilesRef.current;
    const room = MAX_PRODUCT_IMAGES - existingLen - prev.length;
    if (room <= 0) {
      toast.warning(`Maximum ${MAX_PRODUCT_IMAGES} images per product.`);
      return;
    }
    const slice = incoming.slice(0, room);
    const stamped: FileWithPreview[] = slice.map((file) => {
      const f = file as FileWithPreview;
      f.preview = URL.createObjectURL(file);
      return f;
    });
    if (incoming.length > slice.length) {
      toast.info(
        `Only ${slice.length} more image(s) can be added (${MAX_PRODUCT_IMAGES} maximum total).`,
      );
    }
    const next = [...prev, ...stamped];
    validation.setFieldValue("images", next);
    validation.setFieldTouched("images", true);
    setselectedFiles(next);
  };

  const removeNewFileAt = (index: number) => {
    const prev = selectedFilesRef.current;
    const file = prev[index];
    if (file?.preview?.startsWith("blob:")) URL.revokeObjectURL(file.preview);
    const next = prev.filter((_, i) => i !== index);
    validation.setFieldValue("images", next);
    setselectedFiles(next);
  };

  function handleDeleteExistingImage(i: number) {
    const updated = existingFiles.filter((_, index) => index !== i);
    setExistingFiles(updated);
    validation.setFieldValue("existingFiles", updated);
    setDeletedFiles((prev) => {
      const newDeleted = [...prev, i];
      validation.setFieldValue("deletedFiles", newDeleted);
      return newDeleted;
    });
    queueMicrotask(() => void validation.validateField("images"));
  }

  useEffect(() => {
    if (!isEdit || !id) return;

    const fetchRecord = async () => {
      setLoadingRecord(true);
      try {
        const response = await getProduct(Number(id));
        const { success, data } = response;
        if (success) {
          setRecord(data);
          setExistingFiles(data.images || []);
        }
      } catch (err) {
        console.warn(err);
      } finally {
        setLoadingRecord(false);
      }
    };

    fetchRecord();
  }, [id, isEdit]);

  return (
    <div className="page-content">
      <Container fluid>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <BreadCrumb
            title={isEdit ? "Edit Product" : "New Product"}
            pageTitle="Inventory Management"
            backTo={{
              label: "Products",
              path: "/inventory-management/products",
            }}
          />
          {isEdit && loadingRecord && !record ? (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5 my-5">
              <Spinner color="primary" className="mb-2" />
              <span className="small">Loading product…</span>
            </div>
          ) : (
            <>
              <Row className="g-4">
                <Col xl={8} lg={7} className="d-flex flex-column ">
                  <Card className="border shadow-sm">
                    <CardHeader className="border-bottom">
                      <h5 className="card-title mb-1">Product overview</h5>
                      <p className="text-muted small mb-0">
                        Name, catalog material, and specification appear in the
                        catalog and on documents.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div className="mb-3">
                        <Label
                          className="form-label"
                          htmlFor="product-name-input"
                        >
                          Product name
                        </Label>
                        <Input
                          id="product-name-input"
                          type="text"
                          className="form-control"
                          placeholder="Enter product name"
                          name="name"
                          value={validation.values.name || ""}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                          invalid={
                            Boolean(validation.touched.name) &&
                            Boolean(validation.errors.name)
                          }
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.name}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <Row className="g-3">
                        <Col md={6}>
                          <div>
                            <Label className="form-label">Material</Label>

                            <AsyncPaginatedSelect
                              limit={50}
                              defaultOption={
                                record?.materialId != null &&
                                record.materialId > 0
                                  ? {
                                      value: record.materialId,
                                      label:
                                        record.catalogMaterial?.name?.trim() ||
                                        record.catalogMaterialName?.trim() ||
                                        `Material #${record.materialId}`,
                                    }
                                  : undefined
                              }
                              fetchOptions={getMaterialOptions}
                              placeholder="Search materials…"
                              value={validation.values.materialId}
                              onChange={(opt: unknown) => {
                                const o = opt as { value?: number } | null;
                                const v =
                                  o && !Array.isArray(o) ? o.value : undefined;
                                validation.setFieldValue(
                                  "materialId",
                                  v != null ? String(v) : "",
                                );
                              }}
                              onBlur={() =>
                                validation.setFieldTouched("materialId", true)
                              }
                              isInvalid={
                                !!(
                                  validation.touched.materialId &&
                                  validation.errors.materialId
                                )
                              }
                            />
                            {validation.touched.materialId &&
                            validation.errors.materialId ? (
                              <FormFeedback type="invalid" className="d-block">
                                {validation.errors.materialId}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col md={6}>
                          <div>
                            <Label
                              className="form-label"
                              htmlFor="product-specification-input"
                            >
                              Specification
                            </Label>
                            <Input
                              type="text"
                              id="product-specification-input"
                              className="form-control"
                              name="specification"
                              placeholder="Short spec code or label (max 100 chars)"
                              maxLength={100}
                              value={validation.values.specification ?? ""}
                              onBlur={validation.handleBlur}
                              onChange={validation.handleChange}
                              invalid={
                                Boolean(validation.touched.specification) &&
                                Boolean(validation.errors.specification)
                              }
                            />
                            {validation.touched.specification &&
                            validation.errors.specification ? (
                              <FormFeedback type="invalid">
                                {validation.errors.specification}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardHeader className="border-bottom">
                      <h5 className="card-title mb-1">Description</h5>
                      <p className="text-muted small mb-0">
                        Long-form text for the catalog and internal reference.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div>
                        <Label
                          className="form-label"
                          htmlFor="product-description-input"
                        >
                          Description
                        </Label>
                        <Input
                          id="product-description-input"
                          type="textarea"
                          rows={5}
                          className="form-control"
                          placeholder="Enter description"
                          name="description"
                          value={validation.values.description || ""}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                          invalid={
                            Boolean(validation.touched.description) &&
                            Boolean(validation.errors.description)
                          }
                        />
                        {validation.touched.description &&
                        validation.errors.description ? (
                          <FormFeedback type="invalid">
                            {validation.errors.description}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </CardBody>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardHeader className="border-bottom">
                      <h5 className="card-title mb-1">Images</h5>
                      <p className="text-muted small mb-0">
                        At least one image required (max {MAX_PRODUCT_IMAGES}).
                        The{" "}
                        <strong className="fw-semibold text-body">first</strong>{" "}
                        image in the saved order is the thumbnail in lists and
                        pickers.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <FileDropzone
                        accept={PRODUCT_IMAGE_ACCEPT}
                        maxSize={DEFAULT_MAX_FILE_BYTES}
                        invalidTypeMessage="Only PNG, JPG, and JPEG images are accepted"
                        disabled={
                          validation.isSubmitting ||
                          existingFiles.length + selectedFiles.length >=
                            MAX_PRODUCT_IMAGES
                        }
                        invalid={Boolean(
                          validation.touched.images && validation.errors.images,
                        )}
                        errorText={validation.errors.images}
                        description={
                          <>
                            Maximum {MAX_PRODUCT_IMAGES} images total. Up to{" "}
                            {formatFileSize(DEFAULT_MAX_FILE_BYTES)} per file.
                            Order matters: the first image in the saved gallery
                            is the thumbnail. New files upload when you save the
                            product.
                          </>
                        }
                        onDropAccepted={(acceptedFiles) => {
                          appendDroppedFiles(acceptedFiles);
                        }}
                      />

                      {selectedFiles.length > 0 && (
                        <div className="mt-4">
                          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <h6 className="mb-0 fw-semibold">
                              Ready to upload
                              <span className="text-muted fw-normal small ms-2">
                                ({selectedFiles.length}{" "}
                                {selectedFiles.length === 1 ? "file" : "files"})
                              </span>
                            </h6>
                          </div>
                          <Row className="g-3">
                            {selectedFiles.map((f, i) => (
                              <Col
                                key={`${f.name}-${f.size}-${i}`}
                                sm={6}
                                xl={4}
                                className="min-w-0"
                              >
                                <Card className="border shadow-none mb-0 h-100 overflow-hidden">
                                  <CardBody className="p-3 min-w-0">
                                    <div className="d-flex gap-2 gap-sm-3 align-items-start min-w-0 w-100">
                                      <div
                                        className="flex-shrink-0 rounded overflow-hidden bg-light d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "72px",
                                          height: "72px",
                                        }}
                                      >
                                        {isImageMime(f) && f.preview ? (
                                          <img
                                            src={f.preview}
                                            alt=""
                                            className="w-100 h-100"
                                            style={{ objectFit: "cover" }}
                                          />
                                        ) : (
                                          <i className="ri-image-2-line text-secondary fs-24" />
                                        )}
                                      </div>
                                      <div className="min-w-0 flex-grow-1 overflow-hidden">
                                        <p
                                          className="fw-medium small mb-1 text-truncate"
                                          title={f.name}
                                        >
                                          {f.name}
                                        </p>
                                        <p className="text-muted small mb-0 text-truncate">
                                          {formatFileSize(f.size)}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm text-danger p-0 flex-shrink-0 align-self-start lh-1"
                                        title="Remove from list"
                                        aria-label="Remove file"
                                        onClick={() => removeNewFileAt(i)}
                                      >
                                        <i className="ri-close-circle-line fs-18" />
                                      </button>
                                    </div>
                                  </CardBody>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}

                      {existingFiles.length > 0 && (
                        <div className="mt-4 pt-2 border-top">
                          <h6 className="mb-3 fw-semibold text-muted text-uppercase fs-12">
                            Already on this product
                          </h6>
                          <Row className="g-3">
                            {existingFiles.map((f, i) => {
                              const src = resolveImageSrc(f);
                              const label = imageLabel(f);
                              return (
                                <Col
                                  key={`existing-${i}-${label}`}
                                  sm={6}
                                  xl={4}
                                  className="min-w-0"
                                >
                                  <Card className="border shadow-none mb-0 h-100 overflow-hidden">
                                    <CardBody className="p-3 min-w-0">
                                      <div className="d-flex gap-2 gap-sm-3 align-items-start min-w-0 w-100">
                                        <div
                                          className="flex-shrink-0 rounded overflow-hidden bg-light d-flex align-items-center justify-content-center"
                                          style={{
                                            width: "72px",
                                            height: "72px",
                                          }}
                                        >
                                          <img
                                            src={src}
                                            alt=""
                                            className="w-100 h-100"
                                            style={{ objectFit: "cover" }}
                                            onError={(e) => {
                                              (
                                                e.target as HTMLImageElement
                                              ).style.display = "none";
                                            }}
                                          />
                                        </div>
                                        <div className="min-w-0 flex-grow-1 overflow-hidden">
                                          <a
                                            href={src}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="fw-medium small text-body d-block text-truncate"
                                            title={label}
                                          >
                                            {label}
                                          </a>
                                          <span className="text-muted small d-block text-truncate">
                                            Saved image
                                          </span>
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm text-danger p-0 flex-shrink-0 align-self-start lh-1"
                                          title="Remove when you save"
                                          aria-label="Mark image for removal"
                                          onClick={() =>
                                            handleDeleteExistingImage(i)
                                          }
                                        >
                                          <i className="ri-delete-bin-line fs-18" />
                                        </button>
                                      </div>
                                    </CardBody>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={4} lg={5} className="d-flex flex-column">
                  <Card className="border shadow-sm">
                    <CardHeader className="border-bottom">
                      <h5 className="card-title mb-1">Classification</h5>
                      <p className="text-muted small mb-0">
                        Category and product type.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div className="mb-3">
                        <Label className="form-label">Category</Label>
                        <AsyncPaginatedSelect
                          defaultOption={
                            record?.category
                              ? {
                                  value: record.categoryId,
                                  label:
                                    record.category.name ||
                                    record.category.code ||
                                    String(record.categoryId),
                                }
                              : undefined
                          }
                          fetchOptions={getProductCategoryOptions}
                          placeholder="Search categories…"
                          value={validation.values.categoryId}
                          onChange={(val: any) => {
                            validation.setFieldValue("categoryId", val?.value);
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("categoryId", true)
                          }
                          isInvalid={
                            validation.touched.categoryId &&
                            validation.errors.categoryId
                          }
                        />
                        {validation.touched.categoryId &&
                        validation.errors.categoryId ? (
                          <FormFeedback type="invalid">
                            {validation.errors.categoryId}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-0">
                        <Label className="form-label">Product type</Label>
                        <AsyncPaginatedSelect
                          defaultOption={
                            record?.type
                              ? {
                                  value: record.productTypeId,
                                  label:
                                    record.type.name ||
                                    record.type.code ||
                                    String(record.productTypeId),
                                }
                              : undefined
                          }
                          fetchOptions={getProductTypeOptions}
                          placeholder="Search types…"
                          value={validation.values.productTypeId}
                          onChange={(val: any) => {
                            validation.setFieldValue(
                              "productTypeId",
                              val?.value,
                            );
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("productTypeId", true)
                          }
                          isInvalid={
                            validation.touched.productTypeId &&
                            validation.errors.productTypeId
                          }
                        />
                        {validation.touched.productTypeId &&
                        validation.errors.productTypeId ? (
                          <FormFeedback type="invalid">
                            {validation.errors.productTypeId}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </CardBody>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardHeader className="border-bottom">
                      <h5 className="card-title mb-1">Pricing & supplier</h5>
                      <p className="text-muted small mb-0">
                        Costs and manufacturer details.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <Row className="g-3">
                        <Col sm={6}>
                          <div>
                            <Label
                              className="form-label"
                              htmlFor="purchase-cost-input"
                            >
                              Purchase cost
                            </Label>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              className="form-control"
                              id="purchase-cost-input"
                              name="purchaseCost"
                              placeholder="0.00"
                              value={validation.values.purchaseCost}
                              onBlur={validation.handleBlur}
                              onChange={validation.handleChange}
                              invalid={
                                Boolean(validation.touched.purchaseCost) &&
                                Boolean(validation.errors.purchaseCost)
                              }
                            />
                            {validation.touched.purchaseCost &&
                            validation.errors.purchaseCost ? (
                              <FormFeedback type="invalid">
                                {validation.errors.purchaseCost}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div>
                            <Label
                              className="form-label"
                              htmlFor="standard-rate-input"
                            >
                              Standard rate
                            </Label>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              className="form-control"
                              id="standard-rate-input"
                              name="standardRate"
                              placeholder="0.00"
                              value={validation.values.standardRate}
                              onBlur={validation.handleBlur}
                              onChange={validation.handleChange}
                              invalid={
                                Boolean(validation.touched.standardRate) &&
                                Boolean(validation.errors.standardRate)
                              }
                            />
                            {validation.touched.standardRate &&
                            validation.errors.standardRate ? (
                              <FormFeedback type="invalid">
                                {validation.errors.standardRate}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                      </Row>
                      <div className="mb-3 mt-1">
                        <Label
                          className="form-label"
                          htmlFor="manufacturer-name-input"
                        >
                          Manufacturer
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="manufacturer-name-input"
                          name="manufacturer"
                          placeholder="Enter manufacturer"
                          value={validation.values.manufacturer || ""}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                          invalid={
                            Boolean(validation.touched.manufacturer) &&
                            Boolean(validation.errors.manufacturer)
                          }
                        />
                        {validation.touched.manufacturer &&
                        validation.errors.manufacturer ? (
                          <FormFeedback type="invalid">
                            {validation.errors.manufacturer}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-0">
                        <Label className="form-label" htmlFor="brand-input">
                          Brand
                        </Label>
                        <Input
                          type="text"
                          className="form-control"
                          id="brand-input"
                          name="brand"
                          placeholder="Enter brand"
                          value={validation.values.brand || ""}
                          onBlur={validation.handleBlur}
                          onChange={validation.handleChange}
                          invalid={
                            Boolean(validation.touched.brand) &&
                            Boolean(validation.errors.brand)
                          }
                        />
                        {validation.touched.brand && validation.errors.brand ? (
                          <FormFeedback type="invalid">
                            {validation.errors.brand}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </CardBody>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardHeader className="border-bottom">
                      <h5 className="card-title mb-1">Unit & availability</h5>
                      <p className="text-muted small mb-0">
                        Stock unit and whether the product appears in default
                        lists.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div className="mb-3">
                        <Label className="form-label">Unit of measure</Label>
                        <AsyncPaginatedSelect
                          defaultOption={
                            record?.uom
                              ? {
                                  value: record.uomId,
                                  label:
                                    record.uom.code ||
                                    record.uom.name ||
                                    String(record.uomId),
                                }
                              : undefined
                          }
                          fetchOptions={getUomOptions}
                          placeholder="Search UOM…"
                          value={validation.values.uomId}
                          onChange={(val: any) => {
                            validation.setFieldValue("uomId", val?.value);
                          }}
                          onBlur={() =>
                            validation.setFieldTouched("uomId", true)
                          }
                          isInvalid={
                            validation.touched.uomId && validation.errors.uomId
                          }
                        />
                        {validation.touched.uomId && validation.errors.uomId ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.uomId}</div>
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="d-flex align-items-center justify-content-between gap-3 rounded border px-3 py-2 bg-light bg-opacity-50">
                        <div className="min-w-0">
                          <span className="form-label mb-0 fw-medium d-block">
                            Active
                          </span>
                          <span className="text-muted small">
                            Inactive products are hidden from default pickers.
                          </span>
                        </div>
                        <div className="form-check form-switch m-0 flex-shrink-0">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            name="isActive"
                            checked={validation.values.isActive}
                            onBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                            invalid={
                              Boolean(validation.touched.isActive) &&
                              Boolean(validation.errors.isActive)
                            }
                          />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row className="g-4">
                <Col xs={12}>
                  <div className="d-flex flex-wrap justify-content-end gap-2 pt-3 my-3 border-top">
                    <BackButton />
                    <button
                      type="submit"
                      className="btn btn-primary w-sm"
                      disabled={validation.isSubmitting || loadingRecord}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          {isEdit ? "Updating…" : "Submitting…"}
                        </>
                      ) : isEdit ? (
                        "Update"
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Container>
    </div>
  );
};

export default ProductForm;
